import { createFileRoute, type ErrorComponentProps, Link } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { agentQueries } from '@/lib/api/queries/agent'
import { DraftDetailPage } from '@/components/drafts/draft-detail-page'
import { PrdDraftDetailPage } from '@/components/drafts/prd-draft-detail-page'
import { DraftDetailSkeleton } from '@/components/drafts/draft-detail-skeleton'
import { Button } from '@/components/ui/button'

export const Route = createFileRoute(
  '/_authenticated/agents/$agentId_/drafts/$draftId',
)({
  component: DraftDetailRoute,
  errorComponent: DraftDetailError,
})

function DraftDetailRoute() {
  const { agentId, draftId } = Route.useParams()
  return <DraftDetailRouteView agentId={agentId} draftId={draftId} />
}

/**
 * Exported for tests so they can exercise the real fork logic instead of a
 * replica. Takes params as props rather than reading them from the router so
 * it can be mounted under any route in a test harness.
 */
export function DraftDetailRouteView({
  agentId,
  draftId,
}: {
  agentId: string
  draftId: string
}) {
  // D-02: co-fetch the parent agent alongside the draft (the draft query fires
  // inside whichever variant component we render below, so we only fetch the
  // agent at route level).
  const { data: agent, isLoading: agentLoading } = useQuery(agentQueries.detail(agentId))

  // Hot path (user arrived via Drafts tab): agent is cache-hit, skip skeleton.
  // Cold path (direct URL / chat draft-card link): show skeleton until agent resolves.
  if (agentLoading && !agent) return <DraftDetailSkeleton />

  // D-03: missing or non-'product' templateKey -> render the blog variant (safe fallback).
  // Agent-fetch failure (403 / network) also lands here because `agent` stays undefined.
  if (agent?.templateKey === 'product') {
    return <PrdDraftDetailPage agentId={agentId} draftId={draftId} />
  }
  return <DraftDetailPage agentId={agentId} draftId={draftId} />
}

function DraftDetailError({ error }: ErrorComponentProps) {
  const { agentId } = Route.useParams()
  return (
    <div className="flex flex-col items-center justify-center py-16 gap-4">
      <p className="text-destructive font-medium">Failed to load draft</p>
      <p className="text-sm text-muted-foreground max-w-md text-center">
        {error instanceof Error ? error.message : 'An unexpected error occurred'}
      </p>
      <Button variant="outline" asChild>
        <Link to="/agents/$agentId" params={{ agentId }} search={{ tab: 'drafts' }}>
          Back to Drafts
        </Link>
      </Button>
    </div>
  )
}
